import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  HiPhone, HiEnvelope, HiClock,
  HiBuildingOffice, HiOutlineChatBubbleLeftRight,
  HiArrowUpRight
} from 'react-icons/hi2';
import Swal from 'sweetalert2';
import { saveEnquiry } from '../data/enquiriesData';

export default function Contact() {
  const { hash } = useLocation();
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', enquiryType: 'Solar Solutions', city: '', message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (hash) {
      const element = document.getElementById(hash.replace('#', ''));
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const phoneDigits = formData.phone.trim();
    if (!/^\d{10}$/.test(phoneDigits)) {
      Swal.fire({
        title: 'Invalid Phone Number',
        text: 'Please enter a valid 10-digit number.',
        icon: 'warning',
        confirmButtonColor: '#0F5A3A'
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      Swal.fire({
        title: 'Invalid Email Address',
        text: 'Please enter a valid email address.',
        icon: 'warning',
        confirmButtonColor: '#0F5A3A'
      });
      return;
    }

    setIsSubmitted(true);

    const type = 'contact-us';

    const newEnquiry = {
      id: `enq-${Date.now()}`,
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      city: formData.city,
      message: formData.message,
      type: type,
      status: 'pending',
      date: new Date().toISOString().replace('T', ' ').slice(0, 16)
    };

    try {
      await saveEnquiry(newEnquiry, true);
      Swal.fire({
        title: 'Enquiry Submitted!',
        text: `Thank you, ${formData.name}. Your enquiry regarding ${formData.enquiryType} has been submitted successfully. Our team will contact you shortly.`,
        icon: 'success',
        confirmButtonColor: '#0F5A3A'
      });
      setFormData({ name: '', email: '', phone: '', enquiryType: 'Solar Solutions', city: '', message: '' });
      e.target.reset();
    } catch (err) {
      Swal.fire('Error', 'Failed to submit enquiry. Please try again.', 'error');
    } finally {
      setIsSubmitted(false);
    }
  };

  return (
    <div className="page-enter bg-light font-sans pt-16">

      {/* Hero */}
      <section
        className="relative text-white py-28 overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: `linear-gradient(105deg, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.65) 50%, rgba(0, 0, 0, 0.3) 100%), url('/contact us.png')` }}
      >
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-20">
          <span className="eyebrow">Get In Touch</span>
          <h1 className="text-4xl md:text-6xl font-display font-extrabold mb-5 leading-tight">
            Contact <span className="text-gradient-gold">Green Gusto</span>
          </h1>
          <p className="text-base text-white/65 max-w-2xl leading-relaxed">
            Have questions about our power solutions, solar installations, or rental fleet? Get in touch with our expert team today.
          </p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white border-b border-border py-3.5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center gap-2 text-xs text-body-muted">
          <span>Home</span>
          <span className="text-border-dark">/</span>
          <span className="font-semibold text-primary">Contact Us</span>
        </div>
      </div>

      {/* Main Contact Section */}
      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">

          {/* Left: Contact Info Dashboard (Separate Cards) */}
          <div className="lg:col-span-5 space-y-6">

            {/* Headquarters Card */}
            <div className="bg-white border border-border/80 rounded-3xl p-8 shadow-card hover:shadow-premium hover:-translate-y-1 transition-all duration-300 group flex items-start gap-5">
              <div className="w-14 h-14 rounded-2xl card-icon flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform duration-300">
                <HiBuildingOffice className="w-7 h-7" />
              </div>
              <div>
                <span className="text-[10px] font-bold text-accent uppercase tracking-widest block mb-1.5">Corporate Headquarters</span>
                <h3 className="text-lg font-extrabold text-heading mb-2.5">Green Gusto Energy Private Limited</h3>
                <p className="text-sm text-body leading-relaxed">
                  Plot No. B-15/2/67 and B-15/2/68, Waluj MIDC Industrial Area, Near Jonson & Jonsons, Tal. Gangapur, Dist. Chhatrapati Sambhajinagar - 431136, Maharashtra
                </p>
                <a
                  href="https://maps.google.com/?q=Plot+No.+B-15/2/67+and+B-15/2/68,+Waluj+MIDC+Industrial+Area,+Near+Jonson+&+Jonsons,+Tal.+Gangapur,+Dist.+Chhatrapati+Sambhajinagar+-+431136"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-primary hover:text-accent mt-5 transition-colors"
                >
                  View on Google Maps <HiArrowUpRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>

            {/* Channels Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Phone Card */}
              <a href="tel:+919609600600" className="bg-white border border-border/80 rounded-3xl p-7 shadow-card hover:shadow-premium hover:-translate-y-1 transition-all duration-300 group flex flex-col justify-between min-h-[140px]">
                <div className="w-12 h-12 rounded-2xl card-icon flex items-center justify-center mb-5 group-hover:scale-105 transition-transform duration-300">
                  <HiPhone className="w-6 h-6" />
                </div>
                <div>
                  <span className="text-[10px] font-bold text-body-muted uppercase tracking-widest block mb-1">Call Us</span>
                  <span className="text-sm font-bold text-heading group-hover:text-primary transition-colors block">
                    +91 96096 00600
                  </span>
                </div>
              </a>

              {/* Email Card */}
              <a href="mailto:info@green-gusto.com" className="bg-white border border-border/80 rounded-3xl p-7 shadow-card hover:shadow-premium hover:-translate-y-1 transition-all duration-300 group flex flex-col justify-between min-h-[140px]">
                <div className="w-12 h-12 rounded-2xl card-icon flex items-center justify-center mb-5 group-hover:scale-105 transition-transform duration-300">
                  <HiEnvelope className="w-6 h-6" />
                </div>
                <div>
                  <span className="text-[10px] font-bold text-body-muted uppercase tracking-widest block mb-1">Email Us</span>
                  <span className="text-sm font-bold text-heading group-hover:text-primary transition-colors block break-all">
                    info@green-gusto.com
                  </span>
                </div>
              </a>
            </div>

            {/* Operations & Support Card */}
            <div className="bg-white border border-border/80 rounded-3xl p-8 shadow-card hover:shadow-premium hover:-translate-y-1 transition-all duration-300 group flex items-start gap-5">
              <div className="w-14 h-14 rounded-2xl card-icon flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform duration-300">
                <HiClock className="w-7 h-7" />
              </div>
              <div className="flex-grow">
                <span className="text-[10px] font-bold text-primary uppercase tracking-widest block mb-1.5">Service & Support</span>
                <h3 className="text-base font-extrabold text-heading mb-2">Business Operations</h3>
                <p className="text-sm text-body font-semibold">Mon – Sat: 9:00 AM – 6:00 PM</p>
              </div>
            </div>

          </div>

          {/* Right: Enquiry Form */}
          <div id="enquiry" className="lg:col-span-7 bg-charcoal text-white border border-white/5 rounded-3xl p-8 shadow-card hover:shadow-card-hover transition-all duration-300 flex flex-col justify-between w-full group">
            <div className="flex items-center gap-3 mb-2">
              <HiOutlineChatBubbleLeftRight className="w-5 h-5 text-accent group-hover:scale-110 transition-transform duration-300" />
              <span className="text-xs font-bold text-accent uppercase tracking-widest">Send Enquiry Message</span>
            </div>
            <h3 className="text-xl font-display font-extrabold text-white mb-2">How Can We Help You?</h3>
            <p className="text-xs text-white/60 mb-7 leading-relaxed">
              Complete the details below and our specialist team will get back to you within 2 hours.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Full Name</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} required placeholder="Enter your name"
                    className="input-dark" />
                </div>
                <div>
                  <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Email Address</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="Enter your email address"
                    className="input-dark" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Phone Number</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required placeholder="Enter your phone number"
                    className="input-dark" />
                </div>
                <div>
                  <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Enquiry Type</label>
                  <select name="enquiryType" value={formData.enquiryType} onChange={handleChange} required
                    className="input-dark bg-charcoal text-white/85 appearance-none w-full">
                    <option value="Solar Solutions" className="bg-white text-heading">Solar Solutions</option>
                    <option value="Generator Solutions" className="bg-white text-heading">Generator Solutions</option>
                    <option value="Electrical Panels" className="bg-white text-heading">Electrical Panels</option>
                    <option value="Cutting Tools" className="bg-white text-heading">Cutting Tools</option>
                    <option value="Telecom Service" className="bg-white text-heading">Telecom Service</option>
                    <option value="General" className="bg-white text-heading">General</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">City</label>
                <input type="text" name="city" value={formData.city} onChange={handleChange} required placeholder="Enter your city"
                  className="input-dark" />
              </div>
              <div>
                <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Message</label>
                <textarea name="message" value={formData.message} onChange={handleChange} rows={4} required
                  placeholder="Tell us about your requirements..."
                  className="input-dark resize-none" />
              </div>
              <button type="submit" disabled={isSubmitted} className="btn-green rounded-xl w-full justify-center py-3.5 text-sm disabled:opacity-60">
                {isSubmitted ? 'Submitting...' : 'Submit Enquiry'}
                <HiArrowUpRight className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="bg-white border-t border-border w-full">
        <div className="w-full h-[450px] overflow-hidden relative">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d30024.8399383821!2d75.2610584!3d19.83551!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bdb99fb68791145%3A0x3f9161ec9b242139!2sGreen%20Gusto%20Energy%20Private%20Limited!5e0!3m2!1sen!2sin!4v1783158662433!5m2!1sen!2sin"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            title="Green Gusto Location Map"
            className=""
          />
        </div>
      </section>
    </div>
  );
}
